import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs';
import * as GithubActions from './github.actions';

import { GithubFacade, GithubSyncDialogData } from '@mode/github/contract';
import { Store } from '@ngrx/store';
import { isPresent } from '@mode/shared/util-js';
import { dialogDataForContext } from './github.selectors';
import { GITHUB_SYNC_DIALOG_TOKEN, GithubSyncDialog } from './github.types';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';

@Injectable()
export class GithubMenuEffects {
  loadGithubState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        GithubFacade.Actions.datasetEditorHeaderGithubSyncInitialized,
        GithubFacade.Actions.reportEditorHeaderGithubSyncInitialized
      ),
      take(1),
      switchMap(() => {
        // Always stay up to date with the latest state from Report Facade
        // This smells like a missing api call
        return this.reportFacade.headerReportData$;
      }),
      filter(isPresent),
      map((headerReportData) => {
        return GithubActions.githubSyncContextChanged({
          context: {
            syncToGithubForm: headerReportData._forms?.['sync_to_github'],
            githubLink: headerReportData.github_link,
            lastRunAt: headerReportData.last_run_at,
            lastSavedAt: headerReportData.last_saved_at,
            lastSuccessfulSyncAt: headerReportData.last_successful_sync_at,
            syncTargetToken: headerReportData.token,
            syncTargetName: headerReportData.name,
            workspace: headerReportData.account_username,
          },
        });
      })
    );
  });

  startDefinitionContext$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GithubFacade.Actions.definitionEditorHeaderGithubSyncInitialized),
      take(1),
      switchMap((action) => {
        // Always stay up to date with the latest state from Definitions
        // This smells like a missing api call
        return action.context$;
      }),
      filter(isPresent),
      map((context) => {
        return GithubActions.githubSyncContextChanged({ context });
      })
    );
  });

  syncToGithub$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          GithubFacade.Actions.datasetEditorHeaderPushedChangesToGithub,
          GithubFacade.Actions.definitionEditorHeaderPushedChangesToGithub,
          GithubFacade.Actions.reportEditorHeaderPushedChangesToGithub
        ),
        withLatestFrom(this.store.select(dialogDataForContext)),
        filter(([_, dialogData]) => !!dialogData),
        tap(([_, dialogData]) => {
          this.githubSyncDialog.open(dialogData as GithubSyncDialogData);
        })
      ),
    { dispatch: false }
  );

  openGithubSyncDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GithubFacade.Actions.workspaceSettingsPushedChangesToGithub),
        tap((action) => {
          this.githubSyncDialog.open(action.dialogData);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private reportFacade: ReportFacade,
    @Inject(GITHUB_SYNC_DIALOG_TOKEN) private githubSyncDialog: GithubSyncDialog
  ) {}
}
